import { useTranslation } from "react-i18next";
import { css } from "../../../../../../styled-system/css";
import { Flex, HStack, VStack } from "../../../../../../styled-system/jsx";
import { Checkbox } from "../../../../fields/base/Checkbox";
import { CurrencyInput } from "../../../../fields/base/NumberInput";
import { DEFAULT_SB_TARGET_CPC } from "../constants";
import { useSBOptimizationRecommendations } from "../useSBOptimizationRecommendations";
import { ImSpinner2 } from "react-icons/im";
import { useMemo } from "react";

interface CostControlFieldProps {
  state: {
    id?: number;
    costControlTargetCPCEnabled?: boolean;
    costControlTargetCPC?: number | null;
    landingPageProduct?: (string | number)[];
    storeLandingPage?: boolean;
    page?: { value?: string; label?: string };
  };
  onChange: (updates: Record<string, unknown>) => void;
  errors?: Record<string, string>;
  fieldsToValidate?: string[];
  disableFields?: boolean;
  marketplace?: string;
  showRecommendation?: boolean;
}

export function CostControlField({
  state,
  onChange,
  errors,
  fieldsToValidate,
  disableFields = false,
  marketplace,
  showRecommendation = true,
}: CostControlFieldProps) {
  const { t } = useTranslation(["advertising"]);
  const landingPages = useMemo(() => {
    if (state.storeLandingPage && state.page?.value) {
      return [{ pageType: "STORE", url: state.page.value }];
    }
    const productIds = state.landingPageProduct ?? [];
    if (productIds.length === 0) return [];
    return [{ productIds: productIds.map(Number), pageType: "PRODUCT_LIST" }];
  }, [state.storeLandingPage, state.page?.value, state.landingPageProduct]);

  const { data: recommendation, isPending: isLoadingRecommendation } = useSBOptimizationRecommendations({
    marketplace: marketplace ?? "",
    landingPages,
    enabled: showRecommendation && !!state.costControlTargetCPCEnabled,
  });

  return (
    <Flex direction="column" gap="medium">
      <h3
        className={css({
          fontWeight: 560,
          fontSize: "1.6rem",
          color: "gray.500",
        })}
      >
        {t(($) => $.advertising["Cost Control"])}
      </h3>
      <VStack css={{ paddingX: "medium", alignItems: "start" }}>
        <Checkbox
          label={t(($) => $.advertising["Add Target CPC"])}
          value={state.costControlTargetCPCEnabled ?? false}
          isDisabled={disableFields}
          onChange={(checked) => {
            onChange({
              costControlTargetCPCEnabled: checked,
              costControlTargetCPC: checked ? (state.costControlTargetCPC ?? DEFAULT_SB_TARGET_CPC) : null,
            });
          }}
        />
        {state.costControlTargetCPCEnabled && (
          <Flex direction="column" gap="small">
            <CurrencyInput
              label={t(($) => $.advertising["Target Average CPC"])}
              value={state.costControlTargetCPC ?? DEFAULT_SB_TARGET_CPC}
              onChange={(value) => onChange({ costControlTargetCPC: value })}
              minValue={state.id ? 0.5 : 0}
              step={0.01}
              formatOptions={{
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }}
              css={{ width: "200px" }}
              isDisabled={disableFields}
              errorMessage={
                errors?.costControlTargetCPC && fieldsToValidate?.includes("costControlTargetCPC") ?
                  errors.costControlTargetCPC
                : undefined
              }
            />
            {showRecommendation && isLoadingRecommendation && (
              <HStack gap="xsmall" className={css({ color: "gray.400", fontSize: "0.85rem" })}>
                <ImSpinner2 className={css({ animation: "spin" })} />
                <span>{t(($) => $.advertising["Loading recommendation..."])}</span>
              </HStack>
            )}
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- isPending can be true at runtime when enabled toggles */}
            {showRecommendation && recommendation && !isLoadingRecommendation && (
              <HStack gap="small" className={css({ fontSize: "small", color: "gray.500", flexWrap: "wrap" })}>
                <span>
                  {t(($) => $.advertising["Recommended: ${recommended} (min: ${min})"], {
                    recommended: recommendation.recommendedValue.toFixed(2),
                    min: recommendation.minimumValue.toFixed(2),
                  })}
                </span>
                <button
                  type="button"
                  className={css({
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "small",
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: "trellisPalette.0",
                    textDecoration: "underline",
                  })}
                  onClick={() => onChange({ costControlTargetCPC: recommendation.recommendedValue })}
                >
                  {t(($) => $.advertising["Use Recommended Value"])}
                </button>
              </HStack>
            )}
          </Flex>
        )}
      </VStack>
    </Flex>
  );
}
